/**
 * IntensityDashboard — floating overlay panel showing NPS and Physical Strain
 * graphs with a synced playhead and click-to-seek functionality.
 * Uses Canvas 2D for chart rendering (same pattern as Timeline.tsx).
 */

import React, { useRef, useEffect, useCallback } from 'react';
import { useEditorStore } from '../../store/editorStore';
import { intensityToColor } from '../../heatmap/intensityEngine';
import { color } from '../ui/theme';

// ── Constants ────────────────────────────────────────────────────────────────

const CHART_HEIGHT = 70;
const STATS_HEIGHT = 28;
const HEADER_HEIGHT = 24;
const TOTAL_HEIGHT = HEADER_HEIGHT + CHART_HEIGHT + STATS_HEIGHT;
const PADDING_X = 4;

// Colors matching the app theme
const NPS_COLOR = color.accent;         // Magenta
const NPS_FILL = 'oklch(0.78 calc(0.215 * var(--accent-c)) var(--accent-h) / 0.15)';
const STRAIN_COLOR = '#06B6D4';      // Cyan
const STRAIN_FILL = 'rgba(6, 182, 212, 0.12)';
const PLAYHEAD_COLOR = '#ffffff';
const GRID_COLOR = 'oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.15)';
const BG_COLOR = 'rgba(10, 5, 32, 0.85)';
const TEXT_COLOR = color.inkDim;
const MUTED_COLOR = color.inkFaint;

export default function IntensityDashboard() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number>(0);

  const intensityData = useEditorStore(s => s.intensityData);
  const heatmapEnabled = useEditorStore(s => s.heatmapEnabled);
  const playing = useEditorStore(s => s.playback.playing);
  const bpm = useEditorStore(s => s.beatmap?.bpm) || 120;
  const setCurrentBeat = useEditorStore(s => s.setCurrentBeat);
  const setHeatmapEnabled = useEditorStore(s => s.setHeatmapEnabled);

  // ── Click-to-Seek ────────────────────────────────────────────────────────
  const handleClick = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!intensityData || !canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const ratio = clickX / rect.width;
    const beat = ratio * intensityData.totalBeats;
    setCurrentBeat(Math.max(0, Math.min(beat, intensityData.totalBeats)));
  }, [intensityData, setCurrentBeat]);

  // ── Chart Rendering ──────────────────────────────────────────────────────
  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !intensityData) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    const w = rect.width;
    const h = CHART_HEIGHT;

    // Resize canvas to match container
    if (canvas.width !== Math.floor(w * dpr) || canvas.height !== Math.floor(TOTAL_HEIGHT * dpr)) {
      canvas.width = Math.floor(w * dpr);
      canvas.height = Math.floor(TOTAL_HEIGHT * dpr);
      canvas.style.width = w + 'px';
      canvas.style.height = TOTAL_HEIGHT + 'px';
      ctx.scale(dpr, dpr);
    }

    const { beatNPS, beatStrain, beatIntensity, totalBeats, peakNPS, peakStrain } = intensityData;
    const chartW = w - PADDING_X * 2;
    const chartY = HEADER_HEIGHT;

    // Clear
    ctx.clearRect(0, 0, w, TOTAL_HEIGHT);

    // ── Header ─────────────────────────────────────────────────────────
    ctx.fillStyle = BG_COLOR;
    ctx.fillRect(0, 0, w, TOTAL_HEIGHT);

    ctx.fillStyle = TEXT_COLOR;
    ctx.font = '600 10px Montserrat, sans-serif';
    ctx.textAlign = 'left';
    ctx.fillText('\uD83D\uDD25 INTENSITY HEATMAP', 8, 16);

    // Close button area (drawn as text, click handled elsewhere)
    ctx.fillStyle = MUTED_COLOR;
    ctx.textAlign = 'right';
    ctx.font = '500 11px Montserrat, sans-serif';
    ctx.fillText('\u2715', w - 8, 16);

    // ── Intensity color bar (thin strip at top of chart) ───────────────
    const barH = 3;
    for (let px = 0; px < chartW; px++) {
      const beatIdx = Math.floor((px / chartW) * totalBeats);
      const idx = Math.min(beatIdx, totalBeats - 1);
      ctx.fillStyle = intensityToColor(beatIntensity[Math.max(0, idx)]);
      ctx.fillRect(PADDING_X + px, chartY, 1, barH);
    }

    // ── Grid lines (every ~10 seconds) ─────────────────────────────────
    const beatsPerTenSec = (bpm / 60) * 10;
    ctx.strokeStyle = GRID_COLOR;
    ctx.lineWidth = 0.5;
    for (let b = 0; b < totalBeats; b += beatsPerTenSec) {
      const x = PADDING_X + (b / totalBeats) * chartW;
      ctx.beginPath();
      ctx.moveTo(x, chartY + barH);
      ctx.lineTo(x, chartY + h);
      ctx.stroke();

      // Time label
      const sec = (b * 60) / bpm;
      const mm = Math.floor(sec / 60);
      const ss = Math.floor(sec % 60);
      ctx.fillStyle = MUTED_COLOR;
      ctx.textAlign = 'center';
      ctx.font = '400 8px Montserrat, sans-serif';
      ctx.fillText(`${mm}:${ss.toString().padStart(2, '0')}`, x, chartY + h + 10);
    }

    // ── Draw NPS line ──────────────────────────────────────────────────
    const graphTop = chartY + barH + 2;
    const graphH = h - barH - 14; // leave room for time labels

    if (peakNPS > 0) {
      ctx.beginPath();
      ctx.moveTo(PADDING_X, graphTop + graphH);
      for (let px = 0; px <= chartW; px++) {
        const beatIdx = Math.floor((px / chartW) * totalBeats);
        const idx = Math.min(Math.max(0, beatIdx), totalBeats - 1);
        const val = beatNPS[idx] / peakNPS;
        const y = graphTop + graphH - val * graphH;
        ctx.lineTo(PADDING_X + px, y);
      }
      ctx.lineTo(PADDING_X + chartW, graphTop + graphH);
      ctx.closePath();
      ctx.fillStyle = NPS_FILL;
      ctx.fill();
      ctx.strokeStyle = NPS_COLOR;
      ctx.lineWidth = 1.2;
      // Re-draw just the line (not the fill base)
      ctx.beginPath();
      for (let px = 0; px <= chartW; px++) {
        const beatIdx = Math.floor((px / chartW) * totalBeats);
        const idx = Math.min(Math.max(0, beatIdx), totalBeats - 1);
        const val = beatNPS[idx] / peakNPS;
        const y = graphTop + graphH - val * graphH;
        if (px === 0) ctx.moveTo(PADDING_X + px, y);
        else ctx.lineTo(PADDING_X + px, y);
      }
      ctx.stroke();
    }

    // ── Draw Strain line ───────────────────────────────────────────────
    if (peakStrain > 0) {
      ctx.beginPath();
      ctx.moveTo(PADDING_X, graphTop + graphH);
      for (let px = 0; px <= chartW; px++) {
        const beatIdx = Math.floor((px / chartW) * totalBeats);
        const idx = Math.min(Math.max(0, beatIdx), totalBeats - 1);
        const val = beatStrain[idx] / peakStrain;
        const y = graphTop + graphH - val * graphH;
        ctx.lineTo(PADDING_X + px, y);
      }
      ctx.lineTo(PADDING_X + chartW, graphTop + graphH);
      ctx.closePath();
      ctx.fillStyle = STRAIN_FILL;
      ctx.fill();
      ctx.strokeStyle = STRAIN_COLOR;
      ctx.lineWidth = 1;
      ctx.beginPath();
      for (let px = 0; px <= chartW; px++) {
        const beatIdx = Math.floor((px / chartW) * totalBeats);
        const idx = Math.min(Math.max(0, beatIdx), totalBeats - 1);
        const val = beatStrain[idx] / peakStrain;
        const y = graphTop + graphH - val * graphH;
        if (px === 0) ctx.moveTo(PADDING_X + px, y);
        else ctx.lineTo(PADDING_X + px, y);
      }
      ctx.stroke();
    }

    // ── Playhead ───────────────────────────────────────────────────────
    // Read imperatively: draw runs inside a RAF loop, so subscribing
    // reactively to currentBeat would re-render the component at 60fps.
    const currentBeat = useEditorStore.getState().playback.currentBeat;
    if (totalBeats > 0) {
      const phX = PADDING_X + (currentBeat / totalBeats) * chartW;
      ctx.strokeStyle = PLAYHEAD_COLOR;
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(phX, chartY);
      ctx.lineTo(phX, chartY + h);
      ctx.stroke();

      // Small triangle at top
      ctx.fillStyle = PLAYHEAD_COLOR;
      ctx.beginPath();
      ctx.moveTo(phX, chartY);
      ctx.lineTo(phX - 3, chartY - 4);
      ctx.lineTo(phX + 3, chartY - 4);
      ctx.closePath();
      ctx.fill();
    }

    // ── Stats bar at bottom ────────────────────────────────────────────
    const statsY = chartY + h + 2;
    ctx.fillStyle = 'rgba(15, 15, 30, 0.5)';
    ctx.fillRect(0, statsY, w, STATS_HEIGHT);

    ctx.font = '500 9px Montserrat, sans-serif';
    ctx.textAlign = 'left';

    // NPS stats
    ctx.fillStyle = NPS_COLOR;
    ctx.fillText(`Peak NPS: ${peakNPS.toFixed(1)}`, 8, statsY + 16);

    ctx.fillStyle = TEXT_COLOR;
    ctx.fillText(`Avg: ${intensityData.avgNPS.toFixed(1)}`, 100, statsY + 16);

    // Strain stats
    ctx.fillStyle = STRAIN_COLOR;
    ctx.fillText(`Peak Strain: ${peakStrain.toFixed(1)}`, 170, statsY + 16);

    // Legend
    ctx.textAlign = 'right';
    ctx.fillStyle = NPS_COLOR;
    ctx.fillRect(w - 100, statsY + 8, 8, 8);
    ctx.fillStyle = TEXT_COLOR;
    ctx.fillText('NPS', w - 80, statsY + 16);

    ctx.fillStyle = STRAIN_COLOR;
    ctx.fillRect(w - 55, statsY + 8, 8, 8);
    ctx.fillStyle = TEXT_COLOR;
    ctx.fillText('Strain', w - 8, statsY + 16);
  }, [intensityData, bpm]);

  // ── Animation loop ───────────────────────────────────────────────────────
  useEffect(() => {
    let running = true;

    const loop = () => {
      if (!running) return;
      draw();
      if (playing) {
        rafRef.current = requestAnimationFrame(loop);
      }
    };

    loop();

    return () => {
      running = false;
      cancelAnimationFrame(rafRef.current);
    };
  }, [draw, playing]);

  // Redraw on seek while paused (the RAF loop above only runs during playback).
  // Store-level subscription keeps this off the React render path.
  useEffect(() => {
    if (playing) return;
    const unsub = useEditorStore.subscribe((s, prev) => {
      if (s.playback.currentBeat !== prev.playback.currentBeat) draw();
    });
    return unsub;
  }, [playing, draw]);

  // ── Close button handler ─────────────────────────────────────────────────
  const handleCanvasClick = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Check if clicking close button area (top-right corner)
    if (y < HEADER_HEIGHT && x > rect.width - 30) {
      setHeatmapEnabled(false);
      return;
    }

    // Otherwise, seek
    handleClick(e);
  }, [handleClick, setHeatmapEnabled]);

  if (!heatmapEnabled || !intensityData) return null;

  return (
    <div
      ref={containerRef}
      style={panelStyle}
    >
      <canvas
        ref={canvasRef}
        onClick={handleCanvasClick}
        style={canvasStyle}
      />
    </div>
  );
}

// ── Styles ──────────────────────────────────────────────────────────────────

const panelStyle: React.CSSProperties = {
  position: 'absolute',
  // Anchored to the BOTTOM of the viewport (near the transport controls) rather
  // than the top strip.
  bottom: 0,
  left: 0,
  right: 0,
  height: TOTAL_HEIGHT,
  zIndex: 10,
  pointerEvents: 'auto',
  borderTop: '1px solid oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.2)',
  animation: 'fadeIn 0.2s ease',
};

const canvasStyle: React.CSSProperties = {
  width: '100%',
  height: TOTAL_HEIGHT,
  cursor: 'crosshair',
  display: 'block',
};
